import { useCallback, useEffect, useState } from 'react';
import { X, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import Reveal from '@/components/base/Reveal';
import SectionHeading from '@/components/base/SectionHeading';
import NewsCard from '@/pages/home/components/NewsCard';
import { news } from '@/mocks/home';

export default function News() {
  const [active, setActive] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);
  const isOpen = active !== null;

  const close = useCallback(() => setActive(null), []);
  const goPrev = useCallback(
    () => setActive((i) => (i === null ? i : (i - 1 + news.length) % news.length)),
    []
  );
  const goNext = useCallback(
    () => setActive((i) => (i === null ? i : (i + 1) % news.length)),
    []
  );

  // While the lightbox is open: lock the page scroll and wire the keyboard.
  useEffect(() => {
    if (!isOpen) return undefined;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
      if (event.key === 'ArrowLeft') goPrev();
      if (event.key === 'ArrowRight') goNext();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, close, goPrev, goNext]);

  // Reset the expandable content every time a different tip is opened.
  useEffect(() => {
    setExpanded(false);
  }, [active]);

  const current = active !== null ? news[active] : null;

  return (
    <section id="novidades" className="relative w-full bg-background-100 py-20 md:py-28">
      <div className="mx-auto w-full max-w-container px-4 md:px-6 lg:px-10">
        <SectionHeading
          eyebrow="Novidades e Dicas"
          title="Informação útil para viver melhor em condomínio."
          description="Fique a par das últimas novidades da Porta31 e reúna dicas práticas para uma gestão mais simples, transparente e eficiente."
        />

        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {news.map((item, index) => (
            <Reveal key={item.id} delay={(index % 3) * 90} className="h-full">
              <NewsCard
                src={item.src}
                alt={item.alt}
                title={item.title}
                excerpt={item.excerpt}
                category={item.category}
                onOpen={() => setActive(index)}
              />
            </Reveal>
          ))}
        </div>
      </div>

      {current && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={current.title}
          onClick={close}
          className="fixed inset-0 z-[60] flex items-center justify-center bg-secondary-950/85 p-4 backdrop-blur-sm md:p-8"
        >
          <div
            className="animate-fade-up relative flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-background-50 md:flex-row"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              onClick={close}
              aria-label="Fechar"
              className="absolute right-3 top-3 z-10 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-secondary-950/70 text-background-50 transition-colors duration-300 hover:bg-secondary-950"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="relative flex items-center justify-center bg-secondary-950 md:min-h-[380px] md:w-[42%] md:shrink-0">
              <img
                src={current.src}
                alt={current.alt}
                title={`${current.title} · Porta31 Condomínios`}
                className="max-h-[36vh] w-full object-contain md:h-full md:max-h-none md:object-cover"
              />
              <button
                type="button"
                onClick={goPrev}
                aria-label="Dica anterior"
                className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/85 text-foreground-900 transition-colors duration-300 hover:bg-background-50"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={goNext}
                aria-label="Próxima dica"
                className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-background-50/85 text-foreground-900 transition-colors duration-300 hover:bg-background-50"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 md:p-7">
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-accent-900">
                  {current.category}
                </span>
              </div>
              <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground-950 md:text-2xl">
                {current.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-base">
                {current.excerpt}
              </p>

              <button
                type="button"
                onClick={() => setExpanded((value) => !value)}
                aria-expanded={expanded}
                className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-2 text-sm font-semibold whitespace-nowrap text-primary-700 transition-colors duration-300 hover:bg-primary-100"
              >
                {expanded ? 'Ver menos' : 'Ver mais'}
                <ChevronDown
                  className={`h-4 w-4 transition-transform duration-300 ${expanded ? 'rotate-180' : ''}`}
                />
              </button>

              <div
                className={`grid transition-all duration-500 ease-out ${
                  expanded ? 'mt-4 grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                }`}
              >
                <div className="overflow-hidden">
                  <div className="space-y-4 border-t border-background-200 pt-4">
                    {current.content.map((block, index) => (
                      <div key={index}>
                        {block.heading && (
                          <h4 className="font-heading text-sm font-bold leading-snug text-foreground-950 md:text-base">
                            {block.heading}
                          </h4>
                        )}
                        <p className="mt-1 text-sm leading-relaxed text-foreground-600 md:text-base">
                          {block.text}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}